/**
 * Администрирование: пользователи, роли, справочники.
 *
 * Каталог прав НЕ дублируется здесь: он приходит из пакета контрактов
 * (packages/contracts). Сервер отдаёт лишь то, какие права выданы роли,
 * а описания, группы и зависимости — общие для клиента и сервера.
 */

import type { Permission, RoleScope, UserStatus } from "@taskflow/contracts";

import { api } from "../api";
import type { Department } from "../directory/api";

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  position: string | null;
  avatarUrl: string | null;
  primaryDepartmentId: string;
  primaryDepartmentName: string;
  allowedDepartmentIds: string[];
  systemRoleId: string;
  systemRoleName: string;
  status: UserStatus;
  /** §5.4: куда вернёт снятие деактивации. Заполнено только у DEACTIVATED. */
  statusBeforeDeactivation: UserStatus | null;
  lastLoginAtLabel: string | null;
  boardCount: number;
  version: number;
}

/** Ответ users.invitationLink: одноразовая ссылка и её срок. */
export interface InvitationLink {
  id: string;
  email: string;
  link: string;
  expiresAt: string;
  expiresAtLabel: string | null;
}

export interface UserFilters {
  search?: string;
  status?: UserStatus[];
  departmentIds?: string[];
  systemRoleIds?: string[];
  sortField?: string;
  sortDirection?: "asc" | "desc";
}

export interface RoleSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  scope: RoleScope;
  isBuiltIn: boolean;
  permissions: Permission[];
  /** Сколько пользователей или участников досок используют роль.
   *  Нужно при удалении: §6.6 требует переназначения, а не потери доступа. */
  assignmentCount: number;
}

export interface DictionaryItem {
  id: string;
  name: string;
  code?: string | null;
  shortName?: string | null;
  color?: string | null;
  position?: number;
  status: "ACTIVE" | "ARCHIVED";
  version: number;
  /** Сколько объектов ссылается — §7.1 разрешает физическое удаление
   *  только записи без ссылок. */
  usageCount: number;
}

type DictionaryApiKind = "projects" | "priorities" | "task-types";

/** Методы и теги кэша по виду справочника. */
const DICTIONARY = {
  projects: {
    create: "projects.create",
    update: "projects.update",
    archive: "projects.archive",
    restore: "projects.restore",
    tag: "Project",
  },
  priorities: {
    create: "priorities.create",
    update: "priorities.update",
    archive: "priorities.archive",
    restore: "priorities.restore",
    tag: "Priority",
  },
  "task-types": {
    create: "taskTypes.create",
    update: "taskTypes.update",
    archive: "taskTypes.archive",
    restore: "taskTypes.restore",
    tag: "TaskType",
  },
} as const;

export interface DictionaryDraft {
  name?: string;
  /** Только проекты: код обязателен при создании. */
  code?: string;
  shortName?: string | null;
  color?: string | null;
}

export const adminApi = api.injectEndpoints({
  endpoints: (build) => ({
    users: build.query<{ data: AdminUser[] }, UserFilters>({
      query: (params) => ({ method: "users.list", params }),
      providesTags: ["UserList"],
    }),

    user: build.query<AdminUser, string>({
      query: (id) => ({ method: "users.get", params: { id } }),
      providesTags: (_r, _e, id) => [{ type: "User" as const, id }],
    }),

    blockUser: build.mutation<AdminUser, { id: string; reason: string }>({
      query: (params) => ({ method: "users.block", params }),
      invalidatesTags: ["UserList", "User"],
    }),

    unblockUser: build.mutation<AdminUser, { id: string }>({
      query: (params) => ({ method: "users.unblock", params }),
      invalidatesTags: ["UserList", "User"],
    }),

    // A1.5а: потерянный телефон — учётная запись остаётся активной.
    revokeAllUserSessions: build.mutation<{ revoked: number }, { id: string }>({
      query: (params) => ({ method: "users.sessions.revokeAll", params }),
    }),

    deactivateUser: build.mutation<AdminUser, { id: string; reason: string }>({
      query: (params) => ({ method: "users.deactivate", params }),
      invalidatesTags: ["UserList", "User"],
    }),

    /** Удаление из системы: без истории — строкой, с историей — обезличивание. */
    deleteUser: build.mutation<{ id: string; mode: "DELETED" | "ANONYMIZED" }, { id: string }>({
      query: (params) => ({ method: "users.delete", params }),
      invalidatesTags: ["UserList", "User", "Report"],
    }),

    /** Норма нагрузки отдела: меняет проценты «Нагрузки» в отчётности. */
    updateDepartmentNorm: build.mutation<
      { id: string; workloadNorm: number | null },
      { id: string; workloadNorm: number | null }
    >({
      query: (params) => ({ method: "departments.update", params }),
      invalidatesTags: ["Department", "Report"],
    }),

    /** Подразделения: создание, правка названия и подчинённости, удаление пустого. */
    createDepartment: build.mutation<
      Department,
      { code: string; name: string; parentId: string | null }
    >({
      query: (params) => ({ method: "departments.create", params }),
      invalidatesTags: ["Department"],
    }),
    updateDepartment: build.mutation<
      Department,
      { id: string; name?: string; parentId?: string | null }
    >({
      query: (params) => ({ method: "departments.update", params }),
      // Название — подпись вкладок досок и строк отчётов.
      invalidatesTags: ["Department", "Report", "BoardList"],
    }),
    deleteDepartment: build.mutation<{ id: string; deleted: boolean }, { id: string }>({
      query: (params) => ({ method: "departments.delete", params }),
      invalidatesTags: ["Department"],
    }),

    /** §5.4: снятие деактивации. Статус возвращает сервер — он помнит,
     *  каким пользователь был до деактивации. */
    restoreUser: build.mutation<{ id: string; status: UserStatus }, { id: string }>({
      query: (params) => ({ method: "users.restore", params }),
      invalidatesTags: ["UserList", "User"],
    }),

    /** §5.4: повторить письмо, пока приглашённый не стал активным. */
    resendUserInvitation: build.mutation<{ id: string; email: string }, { id: string }>({
      query: (params) => ({ method: "users.resendInvitation", params }),
      invalidatesTags: ["UserList"],
    }),

    /** §5.4: ссылка приглашения для передачи в обход почты — письмо могло
     *  не дойти. Сервер выпускает НОВУЮ ссылку: в базе лежит только хеш
     *  токена, показать прежнюю неоткуда. Прежняя при этом перестаёт
     *  работать, поэтому вызов не идемпотентен и в кэше не хранится. */
    createUserInvitationLink: build.mutation<InvitationLink, { id: string }>({
      query: (params) => ({ method: "users.invitationLink", params }),
      invalidatesTags: ["UserList"],
    }),

    updateUser: build.mutation<
      AdminUser,
      {
        id: string;
        version: number;
        /** Не передаются у приглашённого без регистрации: ФИО он введёт сам. */
        lastName?: string;
        firstName?: string;
        /** null — очистить. */
        middleName: string | null;
        position: string | null;
        /** Не передаётся у приглашённого: приглашение привязано к адресу. */
        email?: string;
        primaryDepartmentId: string;
        allowedDepartmentIds: string[];
      }
    >({
      query: (params) => ({ method: "users.update", params }),
      invalidatesTags: ["UserList", "User", "Me"],
    }),

    setSystemRole: build.mutation<
      { id: string; systemRoleId: string },
      { id: string; roleId: string; version: number }
    >({
      query: (params) => ({ method: "users.setSystemRole", params }),
      invalidatesTags: ["UserList", "User", "Me"],
    }),

    inviteUser: build.mutation<
      { id: string },
      {
        email: string;
        departmentId: string;
        systemRoleId: string;
        boardId: string | null;
        boardRoleId: string | null;
        message: string | null;
        clientRequestId: string;
      }
    >({
      query: (params) => ({ method: "users.invite", params }),
      invalidatesTags: ["UserList"],
    }),

    roles: build.query<{ data: RoleSummary[] }, void>({
      query: () => ({ method: "roles.list" }),
      providesTags: ["Role"],
    }),

    createRole: build.mutation<
      RoleSummary,
      {
        name: string;
        description: string | null;
        scope: RoleScope;
        permissions: Permission[];
        clientRequestId: string;
      }
    >({
      query: (params) => ({ method: "roles.create", params }),
      invalidatesTags: ["Role"],
    }),

    updateRole: build.mutation<
      RoleSummary,
      { id: string; name?: string; description?: string | null; permissions?: Permission[] }
    >({
      query: (params) => ({ method: "roles.update", params }),
      invalidatesTags: ["Role"],
    }),

    deleteRole: build.mutation<{ ok: boolean }, { id: string; reassignTo: string }>({
      // §6.6: удаление роли обязательно переназначает всех, кто ей владел.
      query: (params) => ({ method: "roles.delete", params }),
      invalidatesTags: ["Role", "UserList"],
    }),

    projects: build.query<{ data: DictionaryItem[] }, { includeArchived?: boolean }>({
      query: (params) => ({ method: "projects.list", params }),
      providesTags: ["Project"],
    }),

    priorities: build.query<{ data: DictionaryItem[] }, { includeArchived?: boolean }>({
      query: (params) => ({ method: "priorities.list", params }),
      providesTags: ["Priority"],
    }),

    // ── Правка справочников. Прежде мутаций не было вовсе: «Добавить»
    //    и карандаш не делали ничего, архивация показывала заглушку. ──

    createDictionaryItem: build.mutation<
      DictionaryItem,
      DictionaryDraft & { kind: DictionaryApiKind; name: string; clientRequestId: string }
    >({
      query: ({ kind, ...params }) => ({ method: DICTIONARY[kind].create, params }),
      invalidatesTags: (_result, _error, { kind }) => [DICTIONARY[kind].tag],
    }),

    updateDictionaryItem: build.mutation<
      DictionaryItem,
      DictionaryDraft & { kind: DictionaryApiKind; id: string; version: number }
    >({
      query: ({ kind, ...params }) => ({ method: DICTIONARY[kind].update, params }),
      // Название проекта подписано под заголовком столбца доски —
      // после переименования доска перечитывается.
      invalidatesTags: (_result, _error, { kind }) =>
        kind === "projects" ? [DICTIONARY[kind].tag, "Board"] : [DICTIONARY[kind].tag],
    }),

    archiveDictionaryItem: build.mutation<
      DictionaryItem,
      { kind: DictionaryApiKind; id: string; version: number }
    >({
      query: ({ kind, ...params }) => ({ method: DICTIONARY[kind].archive, params }),
      invalidatesTags: (_result, _error, { kind }) => [DICTIONARY[kind].tag],
    }),

    restoreDictionaryItem: build.mutation<
      DictionaryItem,
      { kind: DictionaryApiKind; id: string; version: number }
    >({
      query: ({ kind, ...params }) => ({ method: DICTIONARY[kind].restore, params }),
      invalidatesTags: (_result, _error, { kind }) => [DICTIONARY[kind].tag],
    }),

    taskTypes: build.query<{ data: DictionaryItem[] }, { includeArchived?: boolean }>({
      query: (params) => ({ method: "taskTypes.list", params }),
      providesTags: ["TaskType"],
    }),
  }),
});

export const {
  useUsersQuery,
  useUserQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
  useDeactivateUserMutation,
  useRevokeAllUserSessionsMutation,
  useRestoreUserMutation,
  useDeleteUserMutation,
  useUpdateDepartmentNormMutation,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useResendUserInvitationMutation,
  useCreateUserInvitationLinkMutation,
  useInviteUserMutation,
  useRolesQuery,
  useUpdateUserMutation,
  useSetSystemRoleMutation,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useProjectsQuery,
  usePrioritiesQuery,
  useTaskTypesQuery,
  useCreateDictionaryItemMutation,
  useUpdateDictionaryItemMutation,
  useArchiveDictionaryItemMutation,
  useRestoreDictionaryItemMutation,
} = adminApi;
